import {
  bottomLinks,
  type FooterLinksSection,
  type FooterLink,
  links,
  socialLinks,
  TEXTS,
  tokushohoLink,
} from './data'
import { type ExperimentManager } from '../../services/ExperimentManager/ExperimentManager'
import { isRecruitingCountry } from '../../utils/countryUtils'
import { buildProductPath } from '../../app/utils/productPages/productRouteHelpers'
import { ProductRouteType } from '../../app/utils/productPages/types'

type LocaleParams = {
  locale: string
}

type CountryParams = {
  country?: string
}

type ExperimentManagerParams = {
  experimentManager: ExperimentManager
}

type FooterParams = LocaleParams & CountryParams

type GetProductsSectionParams = LocaleParams &
  ExperimentManagerParams &
  CountryParams

type GetMediumFooterLinksParams = FooterParams &
  ExperimentManagerParams & {
    withSocialSection: boolean
  }

export function getCanvasLink(locale: string): FooterLink {
  return {
    label: TEXTS.CANVAS_PRINTS,
    href: buildProductPath({
      locale,
      productType: ProductRouteType.CANVAS,
    }),
    hideInSmallScreen: true,
    extraProps: {
      'data-testid': 'canvas-prints-footer',
    },
  }
}

function getProductsSection({
  experimentManager,
  locale,
  country,
}: GetProductsSectionParams): FooterLinksSection {
  const productSection: FooterLinksSection = {
    label: TEXTS.PRODUCTS,
    links:
      country === 'JP'
        ? [links.frameYourPhotos]
        : [links.gift, links.frameYourPhotos, getCanvasLink(locale)],
  }

  if (experimentManager.isEnabled('photo-books')) {
    productSection.links.push(links.photoBooks)
  }

  productSection.links.push(links.galleryWalls)

  productSection.links.push(links.mixtilesArt)

  productSection.links.push(links.business)
  return productSection
}

const commonResourcesLinks = [
  links.cookies,
  bottomLinks.accessibilityButton,
  bottomLinks.terms,
  links.blog,
]

function getResourcesSection({ country }: CountryParams): FooterLinksSection {
  if (isRecruitingCountry(country)) {
    return {
      label: TEXTS.RESOURCES,
      links: [links.careers, links.joinUs, ...commonResourcesLinks],
    }
  } else if (country === 'JP') {
    return {
      label: TEXTS.RESOURCES,
      links: [...commonResourcesLinks, tokushohoLink],
    }
  } else {
    return {
      label: TEXTS.RESOURCES,
      links: commonResourcesLinks,
    }
  }
}

export function getMediumFooterLinks({
  withSocialSection,
  country,
  experimentManager,
  locale,
}: GetMediumFooterLinksParams): FooterLinksSection[] {
  const sections = [getProductsSection({ experimentManager, locale, country })]

  if (withSocialSection) {
    const socialSection = {
      label: TEXTS.FOLLOW_US,
      links: socialLinks,
    }
    sections.push(socialSection)
  }
  sections.push(getResourcesSection({ country }))
  return sections
}

export function getSmallScreenFooterLinks({
  country,
  locale,
}: FooterParams): FooterLink[] {
  if (country === 'JP') {
    return [tokushohoLink]
  }

  const visibleLinks = Object.values(links).filter(
    ({ hideInSmallScreen }) => !hideInSmallScreen
  )

  visibleLinks.push(getCanvasLink(locale))

  if (isRecruitingCountry(country)) {
    visibleLinks.push(links.careers)
  }

  return visibleLinks
}
